/**
 * GatePassPage — Single page with two tabs:
 *   • Manage  — request, approve/reject, see overdue passes, status filter
 *   • Gate    — officer scan UI (Green/Amber/Red), exit/return decisions, denial reason
 *
 * Mounted at: /admin/gate-pass
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  ArrowLeft, Loader2, Plus, DoorOpen, DoorClosed, ShieldCheck, ShieldAlert, RefreshCw,
  CheckCircle2, XCircle, Clock, AlertTriangle, ArrowDownLeft, ArrowUpRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';
import UnifiedScanInput from '@/components/scan/UnifiedScanInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  requested:       'bg-amber-100 text-amber-700 border-amber-300',
  approved:        'bg-blue-100 text-blue-700 border-blue-300',
  active:          'bg-blue-100 text-blue-700 border-blue-300',
  exited:          'bg-indigo-100 text-indigo-700 border-indigo-300',
  returned:        'bg-emerald-100 text-emerald-700 border-emerald-300',
  rejected:        'bg-rose-100 text-rose-700 border-rose-300',
  cancelled:       'bg-slate-100 text-slate-600 border-slate-300',
  denied_at_gate:  'bg-rose-100 text-rose-800 border-rose-400',
  overdue:         'bg-orange-100 text-orange-700 border-orange-400',
  expired:         'bg-slate-200 text-slate-600 border-slate-300',
};

const fetchJson = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

export default function GatePassPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState('manage');
  const [requestOpen, setRequestOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="gate-pass-page">
      <header className="bg-gradient-to-r from-slate-900 via-indigo-900 to-violet-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-white/10">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" /> Gate Pass
            </h1>
            <p className="text-xs text-white/70">Request · Approve · Scan at gate · Track returns</p>
          </div>
          <Button onClick={() => setRequestOpen(true)} className="bg-white text-indigo-900 hover:bg-white/90" data-testid="gp-new-btn">
            <Plus className="h-4 w-4 mr-1" /> New request
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full max-w-md">
            <TabsTrigger value="manage" data-testid="gp-tab-manage">
              <DoorClosed className="h-4 w-4 mr-1" /> Manage
            </TabsTrigger>
            <TabsTrigger value="gate" data-testid="gp-tab-gate">
              <ShieldAlert className="h-4 w-4 mr-1" /> Gate officer
            </TabsTrigger>
          </TabsList>

          <TabsContent value="manage" className="mt-4 space-y-4">
            <ManageView />
          </TabsContent>
          <TabsContent value="gate" className="mt-4">
            <GateOfficerView />
          </TabsContent>
        </Tabs>
      </main>

      {requestOpen && (
        <RequestPassDialog onClose={() => { setRequestOpen(false); qc.invalidateQueries({ queryKey: ['gate-passes'] }); }} />
      )}
    </div>
  );
}


function ManageView() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('open');

  const { data: dash } = useQuery({
    queryKey: ['gate-passes-dashboard'],
    queryFn: () => fetchJson('/api/gate-passes/dashboard'),
    refetchInterval: 30000,
  });
  const { data: list, isLoading } = useQuery({
    queryKey: ['gate-passes', statusFilter],
    queryFn: () => fetchJson(`/api/gate-passes${statusFilter === 'open' ? '?open_only=true' : `?status=${statusFilter}`}&limit=100`),
    refetchInterval: 30000,
  });

  const mutate = useMutation({
    mutationFn: ({ id, action, body }) => fetchJson(`/api/gate-passes/${id}/${action}`, { method: 'POST', body: JSON.stringify(body || {}) }),
    onSuccess: () => {
      toast.success('Pass updated');
      qc.invalidateQueries({ queryKey: ['gate-passes'] });
      qc.invalidateQueries({ queryKey: ['gate-passes-dashboard'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const approveQ = (p) => mutate.mutate({ id: p.id, action: 'approve' });
  const cancelQ = (p) => mutate.mutate({ id: p.id, action: 'cancel' });
  const rejectQ = (p) => {
    const reason = window.prompt('Reason for rejection:');
    if (!reason) return;
    mutate.mutate({ id: p.id, action: 'reject', body: { reason } });
  };

  return (
    <>
      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="gp-stats">
        <StatTile label="Open" value={dash?.open ?? 0} icon={DoorOpen} color="text-indigo-600" />
        <StatTile label="Awaiting approval" value={dash?.pending_approval ?? 0} icon={Clock} color="text-amber-600" />
        <StatTile label="Overdue" value={dash?.overdue ?? 0} icon={AlertTriangle} color="text-rose-600" highlight={dash?.overdue > 0} />
        <StatTile label="Returned today" value={dash?.by_status?.returned ?? 0} icon={CheckCircle2} color="text-emerald-600" />
      </div>

      <div className="flex items-center gap-2 flex-wrap mt-2">
        <Label className="text-xs uppercase tracking-wider text-slate-500">Status:</Label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="gp-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">All open</SelectItem>
            <SelectItem value="requested">Awaiting approval</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="exited">Exited (still out)</SelectItem>
            <SelectItem value="returned">Returned</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="denied_at_gate">Denied at gate</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? (
            <div className="p-6 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</div>
          ) : (list?.passes || []).length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">No passes match this filter.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b">
                <tr>
                  <th className="text-left p-3">Person</th>
                  <th className="text-left p-3">Reason</th>
                  <th className="text-left p-3">Expected return</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Approver</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {list.passes.map((p) => (
                  <tr key={p.id} className="border-b hover:bg-slate-50" data-testid={`gp-row-${p.id}`}>
                    <td className="p-3">
                      <div className="font-medium">{p.person_name || p.person_id}</div>
                      <div className="text-xs text-slate-500 capitalize">{p.person_kind} · {p.context_type}</div>
                    </td>
                    <td className="p-3 text-xs">{p.reason}</td>
                    <td className="p-3 text-xs">{p.expected_return ? new Date(p.expected_return).toLocaleString() : '—'}</td>
                    <td className="p-3">
                      <Badge variant="outline" className={STATUS_STYLE[p.status] || ''}>
                        {(p.status || '').replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="p-3 text-xs text-slate-500">{p.approved_by_email || '—'}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {p.status === 'requested' && (
                        <>
                          <Button size="sm" variant="outline" className="mr-1 text-emerald-700 border-emerald-300" onClick={() => approveQ(p)} data-testid={`gp-approve-${p.id}`}>Approve</Button>
                          <Button size="sm" variant="outline" className="text-rose-700 border-rose-300" onClick={() => rejectQ(p)} data-testid={`gp-reject-${p.id}`}>Reject</Button>
                        </>
                      )}
                      {['approved', 'active'].includes(p.status) && (
                        <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => cancelQ(p)} data-testid={`gp-cancel-${p.id}`}>Cancel</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </>
  );
}


function StatTile({ label, value, icon: Icon, color, highlight }) {
  return (
    <Card className={highlight ? 'border-rose-300 border-2 bg-rose-50/40' : ''}>
      <CardContent className="p-4">
        <div className={`text-[10px] uppercase tracking-wider flex items-center gap-1 ${color}`}>
          <Icon className="h-3 w-3" /> {label}
        </div>
        <div className={`text-3xl font-bold ${color}`}>{value}</div>
      </CardContent>
    </Card>
  );
}


function GateOfficerView() {
  const [lastDecision, setLastDecision] = useState(null);
  const [busy, setBusy] = useState(false);
  const [denyOpen, setDenyOpen] = useState(false);
  const [pendingScan, setPendingScan] = useState(null);
  const [denyReason, setDenyReason] = useState('');

  const handleScan = async (decision, value, reason = '') => {
    setBusy(true);
    try {
      const data = await fetchJson('/api/gate-passes/scan', {
        method: 'POST',
        body: JSON.stringify({ value, decision, deny_reason: reason }),
      });
      setLastDecision(data);
      const t = data.color === 'green' ? 'success' : data.color === 'amber' ? 'warning' : 'error';
      toast[t === 'warning' ? 'message' : t](data.message);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" /> Scan a badge
          </CardTitle>
        </CardHeader>
        <CardContent>
          <UnifiedScanInput
            autoSubmitOnScan={false}
            onIdentified={(data) => {
              // The unified input already identified the person — now submit
              // automatically as 'auto' (backend decides exit vs return).
              setPendingScan(data.value);
              handleScan('auto', data.value);
            }}
          />
          {/* Manual override buttons */}
          {lastDecision?.profile && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="sm" variant="outline"
                onClick={() => handleScan('exit', lastDecision.value || pendingScan)}
                disabled={busy}
                data-testid="gp-force-exit"
              >
                <ArrowUpRight className="h-4 w-4 mr-1" /> Force exit
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={() => handleScan('return', lastDecision.value || pendingScan)}
                disabled={busy}
                data-testid="gp-force-return"
              >
                <ArrowDownLeft className="h-4 w-4 mr-1" /> Mark return
              </Button>
              <Button
                size="sm" variant="outline" className="text-rose-700 border-rose-300"
                onClick={() => setDenyOpen(true)}
                disabled={busy}
                data-testid="gp-force-deny"
              >
                <XCircle className="h-4 w-4 mr-1" /> Deny exit
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className={lastDecision ? `border-2 ${lastDecision.color === 'green' ? 'border-emerald-400 bg-emerald-50' : lastDecision.color === 'amber' ? 'border-amber-400 bg-amber-50' : 'border-rose-400 bg-rose-50'}` : ''}>
        <CardHeader>
          <CardTitle className="text-sm">Decision</CardTitle>
        </CardHeader>
        <CardContent>
          {!lastDecision ? (
            <p className="text-sm text-slate-400 text-center py-8">Scan a badge to begin.</p>
          ) : (
            <div className="space-y-3">
              <div className={`text-xl font-bold ${lastDecision.color === 'green' ? 'text-emerald-700' : lastDecision.color === 'amber' ? 'text-amber-700' : 'text-rose-700'}`}>
                {lastDecision.color === 'green' ? '🟢 GO' : lastDecision.color === 'amber' ? '🟡 CHECK' : '🔴 STOP'}
              </div>
              <div className="text-sm">{lastDecision.message}</div>
              {lastDecision.profile && (
                <div className="text-sm bg-white p-2 rounded border">
                  <div className="font-medium">{lastDecision.profile.name}</div>
                  <div className="text-xs text-slate-600 capitalize">
                    {lastDecision.profile.kind}
                    {lastDecision.profile.class_name && ` · ${lastDecision.profile.class_name}`}
                    {lastDecision.profile.admission_number && ` · #${lastDecision.profile.admission_number}`}
                  </div>
                </div>
              )}
              {lastDecision.pass && (
                <div className="text-xs text-slate-600 bg-white p-2 rounded border">
                  <div><strong>Reason:</strong> {lastDecision.pass.reason}</div>
                  <div><strong>Expected return:</strong> {new Date(lastDecision.pass.expected_return).toLocaleString()}</div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={denyOpen} onOpenChange={setDenyOpen}>
        <DialogContent data-testid="gp-deny-dialog">
          <DialogHeader>
            <DialogTitle>Deny exit — provide reason</DialogTitle>
          </DialogHeader>
          <Textarea
            value={denyReason} onChange={(e) => setDenyReason(e.target.value)}
            placeholder="Reason for denial (logged to audit trail)"
            rows={4} data-testid="gp-deny-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDenyOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={!denyReason.trim()}
              onClick={() => {
                handleScan('deny', lastDecision?.value || pendingScan, denyReason.trim());
                setDenyOpen(false); setDenyReason('');
              }}
              data-testid="gp-deny-confirm"
            >
              Confirm denial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function RequestPassDialog({ onClose }) {
  const [identified, setIdentified] = useState(null);
  const [reason, setReason] = useState('');
  const [expectedReturn, setExpectedReturn] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!identified) { toast.error('Please scan/identify the person'); return; }
    if (!reason.trim() || !expectedReturn) { toast.error('Reason and expected return time are required'); return; }
    setSubmitting(true);
    try {
      await fetchJson('/api/gate-passes', {
        method: 'POST',
        body: JSON.stringify({
          person_id: identified.profile.id,
          person_kind: identified.profile.kind,
          person_name: identified.profile.name,
          context_type: identified.profile.context_type || 'school',
          context_id: identified.profile.tenant_id || identified.profile.event_id || null,
          reason,
          expected_return: new Date(expectedReturn).toISOString(),
        }),
      });
      toast.success('Gate pass requested · awaiting approval');
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="gp-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><DoorOpen className="h-4 w-4" /> Request gate pass</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">1 · Identify</Label>
            <div className="mt-2"><UnifiedScanInput onIdentified={setIdentified} /></div>
          </div>
          {identified && (
            <>
              <div>
                <Label>Reason *</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. medical appointment, parent visit" data-testid="gp-req-reason" />
              </div>
              <div>
                <Label>Expected return *</Label>
                <Input
                  type="datetime-local" value={expectedReturn}
                  onChange={(e) => setExpectedReturn(e.target.value)}
                  min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                  data-testid="gp-req-return"
                />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !identified} data-testid="gp-req-submit">
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Submit request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
